/**
 * 音色库弹层（TTS 刀T3）。角色卡点「选择音色」打开：
 *  - 浏览/试听内置**可商用系统音色**（24 条）；
 *  - **文字造声**（声音设计，零版权风险原创新声）；
 *  - **上传有权样本克隆**（声音复刻）；
 *  - **商用素材市场**：占位（D-81，未来里程碑）。
 * 选中后点「确定」回写角色 voice 槽。只渲染 + 回调，写 store 由父级负责。
 */
import { useRef, useState } from 'react';
import { Sparkles, Upload, Volume2 } from 'lucide-react';

import { Modal } from '../../ui/Modal';
import { SYSTEM_VOICES, type VoiceProfile } from '../../model/voice';
import { generationProvider } from './generationService';

function readDataUrl(file: File): Promise<string> {
  return new Promise((resolve) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.readAsDataURL(file);
  });
}

/** 探测音频时长（秒）；失败返回 null。 */
function probeDuration(src: string): Promise<number | null> {
  return new Promise((resolve) => {
    const el = new Audio();
    el.preload = 'metadata';
    el.onloadedmetadata = () => resolve(Number.isFinite(el.duration) ? el.duration : null);
    el.onerror = () => resolve(null);
    el.src = src;
  });
}

const sourceBadge: Record<string, string> = {
  system: '系统',
  design: '已造',
  clone: '已克隆',
  market: '付费',
};

interface VoicePickerProps {
  open: boolean;
  current?: VoiceProfile | undefined;
  /** 角色小传（P-C3）：传入则提供“用小传造声”一键 */
  bioHint?: string | undefined;
  onClose: () => void;
  onPick: (v: VoiceProfile) => void;
}

export function VoicePicker({ open, current, bioHint, onClose, onPick }: VoicePickerProps) {
  const [custom, setCustom] = useState<VoiceProfile[]>([]);
  const [selectedId, setSelectedId] = useState<string | undefined>(current?.id);
  const [busy, setBusy] = useState<null | 'design' | 'clone'>(null);
  const [designPrompt, setDesignPrompt] = useState('');
  const [note, setNote] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const list = [...custom, ...SYSTEM_VOICES];
  const chosen = list.find((v) => v.id === selectedId) ?? current;

  /** 试听：走 TTS 合成一句样音播放（mock 为静音占位，接真后真出声）。 */
  const playPreview = async (v: VoiceProfile) => {
    try {
      const a = await generationProvider.tts({ text: `这是${v.name}的音色试听。`, voiceId: v.voiceId, emotion: '中性' });
      audioRef.current?.pause();
      const el = new Audio(a.url);
      audioRef.current = el;
      void el.play();
    } catch {
      /* 忽略：无音频时静默 */
    }
  };

  const doDesign = async () => {
    const p = designPrompt.trim();
    if (p === '' || busy !== null) {
      return;
    }
    setBusy('design');
    const v = await generationProvider.designVoice({ prompt: p, name: p.slice(0, 8) });
    setCustom((c) => [v, ...c]);
    setSelectedId(v.id);
    setBusy(null);
    setNote(`已造声“${v.name}”（占位）— 接真实后端后才有真声线`);
    setDesignPrompt('');
  };

  const doClone = async (file: File) => {
    if (busy !== null) {
      return;
    }
    setBusy('clone');
    const dataUrl = await readDataUrl(file);
    const dur = await probeDuration(dataUrl);
    if (dur !== null && dur < 3) {
      setBusy(null);
      setNote('样本太短（需 3–10 秒），请换一段');
      return;
    }
    const v = await generationProvider.cloneVoice({ sampleDataUrl: dataUrl, name: file.name.replace(/\.[^.]+$/, '').slice(0, 10) });
    setCustom((c) => [v, ...c]);
    setSelectedId(v.id);
    setBusy(null);
    setNote(
      dur !== null && dur > 10
        ? `已登记克隆“${v.name}”（样本 ${Math.round(dur)}s 超 10s，接真后端时会裁剪）`
        : `已登记克隆“${v.name}”（占位）— 接真实后端后才有真声线`,
    );
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      placement="right"
      tone="neutral"
      title="音色库"
      summary="一个角色绑定一条声线、全剧统一；可试听/文字造声/上传有权样本克隆。"
      footer={
        <>
          <button type="button" className="btn-ghost px-3 py-1.5 text-ui" onClick={onClose}>
            取消
          </button>
          <button
            type="button"
            className="btn-primary px-3 py-1.5 text-ui disabled:opacity-40"
            disabled={chosen === undefined}
            onClick={() => {
              if (chosen !== undefined) {
                onPick(chosen);
              }
              onClose();
            }}
          >
            确定{chosen !== undefined ? `（${chosen.name}）` : ''}
          </button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {/* 造声 / 克隆 / 市场占位 */}
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center gap-1.5">
            <input
              value={designPrompt}
              placeholder="文字造声：如“低沉磁性的中年男声”（声音设计，零版权）"
              className="min-w-0 flex-1 rounded-control bg-surface-base px-2.5 py-1.5 text-uiSm text-ink-primary outline-none"
              onChange={(e) => setDesignPrompt(e.target.value)}
            />
            <button
              type="button"
              className="inline-flex items-center gap-1 rounded-control bg-accent/15 px-2 py-1.5 text-uiSm font-semibold text-accent disabled:opacity-40"
              disabled={busy !== null}
              onClick={() => void doDesign()}
            >
              <Sparkles size={13} />
              {busy === 'design' ? '造声中…' : '造声'}
            </button>
            {bioHint !== undefined && bioHint !== '' && (
              <button
                type="button"
                className="rounded-control bg-surface-base px-2 py-1.5 text-uiSm text-ink-secondary transition-colors hover:text-accent"
                title="用角色小传作为造声描述填入"
                onClick={() => setDesignPrompt(bioHint)}
              >
                用小传
              </button>
            )}
          </div>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              className="inline-flex items-center gap-1 rounded-control bg-surface-base px-2.5 py-1.5 text-uiSm text-ink-secondary transition-colors hover:text-ink-primary disabled:opacity-40"
              disabled={busy !== null}
              onClick={() => fileRef.current?.click()}
            >
              <Upload size={13} />
              {busy === 'clone' ? '克隆中…' : '上传有权样本克隆'}
            </button>
            <span className="text-uiSm text-ink-faint">上传原始有权人声样本·当前为占位（接真后端后声纹克隆）</span>
            <input
              ref={fileRef}
              type="file"
              accept="audio/*"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f !== undefined) {
                  void doClone(f);
                }
                e.currentTarget.value = '';
              }}
            />
          </div>
          <div className="rounded-control border border-dashed border-surface-line px-2.5 py-1.5 text-uiSm text-ink-faint">
            商用音色市场（带版权·付费）· 即将开放 · 见技术债 D-81
          </div>
          {note !== '' && <div className="text-uiSm text-state-info">{note}</div>}
        </div>

        {/* 音色列表 */}
        <div className="grid grid-cols-2 gap-1.5">
          {list.map((v) => {
            const on = v.id === chosen?.id;
            return (
              <div
                key={v.id}
                role="button"
                tabIndex={0}
                onClick={() => setSelectedId(v.id)}
                className={`flex flex-col items-start gap-0.5 rounded-control border px-2.5 py-2 text-left transition-colors ${
                  on ? 'border-accent bg-accent/10' : 'border-surface-line hover:border-surface-edge'
                }`}
              >
                <div className="flex w-full items-center gap-1.5">
                  <span className="text-ui font-semibold text-ink-primary">{v.name}</span>
                  <span className="rounded bg-surface-base px-1 text-[10px] text-ink-faint">{sourceBadge[v.source] ?? v.source}</span>
                  <button
                    type="button"
                    title="试听"
                    aria-label="试听"
                    onClick={(e) => {
                      e.stopPropagation();
                      void playPreview(v);
                    }}
                    className="ml-auto flex h-6 w-6 items-center justify-center rounded-control bg-surface-base text-ink-secondary transition-colors hover:text-accent"
                  >
                    <Volume2 size={13} />
                  </button>
                </div>
                <span className="text-uiSm text-ink-muted">
                  {v.gender} · {v.age}
                  {v.license.commercial ? '' : ' · 非商用'}
                </span>
                {v.desc !== '' && <span className="line-clamp-2 text-uiSm text-ink-faint">{v.desc}</span>}
              </div>
            );
          })}
        </div>
      </div>
    </Modal>
  );
}
